import type {ScannedJobResult} from "./scanJobDetails.ts";
import type {OverallProblemIndicators, ScannedGenericResult} from "../../scan-model/ScanResult.tsx";
import type {PythonProblemIndicator} from "./ProblemIndicators.ts";

/**
 * Take python data from source and modify target to contain it
 */
function mergePython(source: PythonProblemIndicator | undefined, target: PythonProblemIndicator | undefined): PythonProblemIndicator | undefined {
    if (source == undefined) return target
    if (target == undefined) return {
        interpreters: [...source.interpreters],
        interpreterComponentCount: {...source.interpreterComponentCount},
        quickImportScan: [...source.quickImportScan],
        usesCursor: source.usesCursor
    }
    source.interpreters.forEach(i => {
        if (!target.interpreters.includes(i)) target.interpreters.push(i)
    })
    Object.entries(source.interpreterComponentCount).forEach(([interpreter, count]) => {
        target.interpreterComponentCount[interpreter] = (target.interpreterComponentCount[interpreter] || 0) + count
    })
    source.quickImportScan.forEach(i => {
        if (!target.quickImportScan.includes(i)) target.quickImportScan.push(i)
    })
    target.usesCursor = target.usesCursor || source.usesCursor
    return target
}

function mergeComponentCount(source: Record<string, number>, target: Record<string, number>) {
    Object.keys(source).forEach((sourceId) => {
        if (target[sourceId] === undefined) {
            target[sourceId] = source[sourceId]
        } else {
            target[sourceId] += source[sourceId]
        }
    })
}

function mergeSharedJobs(source: ScannedJobResult, target: ScannedGenericResult) {
    if (!source.problemIndicators) return
    source.problemIndicators.usedSharedJobs?.forEach(({sharedJob}) => {
        const key = sharedJob.packageName + "." + sharedJob.jobName + "." + sharedJob.revision
        if (target.problemIndicators.sharedJobCount[key] === undefined) {
            target.problemIndicators.sharedJobCount[key] = 1
            target.problemIndicators.usedSharedJobs.push(sharedJob)
        } else {
            target.problemIndicators.sharedJobCount[key]++
        }
    })
}

function mergeDBQueries(source: ScannedJobResult, target: ScannedGenericResult) {
    if (!source.problemIndicators) return
    Object.entries(source.problemIndicators.databaseTypeCount ?? {}).forEach(([driver, count]) => {
        target.problemIndicators.databaseTypeCount[driver] = (target.problemIndicators.databaseTypeCount[driver] ?? 0) + count
    })
    Object.entries(source.problemIndicators.databaseTypeConnectionOptions ?? {}).forEach(([driver, connectionOptions]) => {
        target.problemIndicators.databaseTypeConnectionOptions[driver] ??= []
        connectionOptions.forEach((option) => {
            if (!target.problemIndicators.databaseTypeConnectionOptions[driver].includes(option)) target.problemIndicators.databaseTypeConnectionOptions[driver].push(option)
        })
    })
}

function mergeStringPasswords(job: ScannedJobResult, target: OverallProblemIndicators) {
    if (job.problemIndicators?.componentsWithStringPasswords === undefined) return
    target.hasStringPasswords = true
    target.jobsWithStringPasswords.push(job.jobName)
}

export function mergeProblemResults(source: ScannedJobResult[], target: ScannedGenericResult) {
    source.forEach((jobResult) => {
        mergeComponentCount(jobResult.componentCount, target.componentCount)
        target.problemIndicators.python = mergePython(jobResult.problemIndicators?.python, target.problemIndicators.python)
        target.problemIndicators.gridExportComponents += jobResult.problemIndicators?.gridExportComponents?.length ?? 0
        mergeSharedJobs(jobResult, target)
        mergeDBQueries(jobResult, target)
        mergeStringPasswords(jobResult, target.problemIndicators)
    })
}

export function blankProblem(): OverallProblemIndicators {
    return {
        python: undefined,
        gridExportComponents: 0,
        sharedJobCount: {},
        usedSharedJobs: [],
        databaseTypeCount: {},
        databaseTypeConnectionOptions: {},
        hasStringPasswords: false,
        jobsWithStringPasswords: []
    }
}